import React from 'react';
import { clsx } from 'clsx';
import { signedToneClass, moneyAbbrev } from './history-utils';

// LR-498: the "By date" master list — a chronological blotter across all counterparties, grouped
// under sticky day headers. One row per CP's run (MTM) / posting (Cash Pool) that day. Selecting a
// row drives the right pane to that single entry's line items (the date axis lives here, not there).

export interface DateEventRow {
  /** Deep-link selection key — the run id (MTM) / margin-call id (Cash Pool), as a string. */
  id: string;
  time: string;
  /** LR-679: hover text for the time cell — the canonical UTC stamp plus the viewer's own clock.
   *  The column is 38px wide, so the zone cannot be spelled out inline here. */
  timeTitle?: string;
  cp: string;
  net: number;
  /** Currency code — shown as a tag so same-timestamp rows of one CP (one per currency) are
   *  distinguishable; the value itself is rendered code-less via moneyAbbrev (T1-1). */
  ccy: string;
  /** True when |net| is below the CP's MTA → no cash transfers; the row is muted (T1-3, MTM only). */
  belowMta?: boolean;
}

export interface DateGroup {
  label: string;
  rows: DateEventRow[];
}

export interface DateGroupedListProps {
  groups: DateGroup[];
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Empty-state copy when the (possibly filtered) list has no rows. */
  emptyLabel?: string;
}

export const DateGroupedList: React.FC<DateGroupedListProps> = ({
  groups,
  selectedId,
  onSelect,
  emptyLabel = 'No results.',
}) => {
  if (groups.length === 0) {
    return <p className="px-[18px] py-3 text-sm text-lr-faint">{emptyLabel}</p>;
  }
  return (
    <div>
      {groups.map((group) => (
        <section key={group.label}>
          <div className="sticky top-0 z-[2] border-b border-lr-border bg-lr-bg px-[18px] py-[7px] text-[10.5px] font-semibold uppercase tracking-[.07em] text-lr-faint">
            {group.label}
          </div>
          {group.rows.map((row) => {
            const selected = row.id === selectedId;
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => onSelect(row.id)}
                aria-current={selected}
                className={clsx(
                  'flex w-full items-center gap-3 border-b border-lr-border/60 border-l-[3px] py-3 pl-[15px] pr-[18px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent',
                  selected
                    ? 'border-l-lr-accent bg-lr-row-active'
                    : 'border-l-transparent hover:bg-lr-surface-2'
                )}
              >
                <span
                  className="w-[38px] shrink-0 font-mono text-[12.5px] font-medium text-lr-faint"
                  title={row.timeTitle}
                >
                  {row.time}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-lr-fg">
                  {row.cp}
                </span>
                <span className="shrink-0 rounded bg-[#f1f3f5] px-1.5 py-0.5 font-mono text-[10.5px] text-lr-fg-2">
                  {row.ccy}
                </span>
                <div className="flex shrink-0 flex-col items-end">
                  <span
                    className={clsx(
                      'font-mono text-[13.5px] font-semibold',
                      row.belowMta ? 'text-lr-faint' : signedToneClass(row.net)
                    )}
                  >
                    {moneyAbbrev(row.net)}
                  </span>
                  {row.belowMta && (
                    <span className="text-[9.5px] uppercase tracking-[.03em] text-lr-faint">
                      below MTA
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
};
